// src/pages/admin/AdminTeamAccess.jsx
// « Équipe & accès » — l'admin délègue des coins (espaces) à ses membres team.
// Écrit profiles.team_coins (colonne protégée : seul l'admin peut la modifier,
// l'admin bypasse le trigger anti-escalade). Presets = onboarding en un clic.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { COINS, PRESETS } from "../../config/teamCoins";

export default function AdminTeamAccess() {
  const { profile, profileLoading } = useAuth();
  const [members, setMembers] = useState([]);
  const [draft, setDraft] = useState({}); // { memberId: string[] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId] = useState(null);

  const isAdmin = profile?.role === "admin";

  async function load() {
    setLoading(true);
    setError("");
    const { data, error: e } = await supabase
      .from("profiles")
      .select("id, first_name, last_name, email, role, team_coins")
      .eq("role", "team")
      .order("created_at", { ascending: false });
    if (e) { setError(e.message); setMembers([]); }
    else {
      setMembers(data || []);
      setDraft(Object.fromEntries((data || []).map((m) => [m.id, [...(m.team_coins || [])]])));
    }
    setLoading(false);
  }

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const toggle = (id, key) => {
    setSavedId(null);
    setDraft((d) => {
      const cur = new Set(d[id] || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...d, [id]: [...cur] };
    });
  };

  const applyPreset = (id, coins) => {
    setSavedId(null);
    setDraft((d) => {
      const merged = new Set([...(d[id] || []), ...coins]);
      return { ...d, [id]: [...merged] };
    });
  };

  const clearAll = (id) => { setSavedId(null); setDraft((d) => ({ ...d, [id]: [] })); };

  const dirty = (m) => {
    const a = [...(m.team_coins || [])].sort().join(",");
    const b = [...(draft[m.id] || [])].sort().join(",");
    return a !== b;
  };

  async function save(m) {
    setSavingId(m.id);
    setError("");
    const coins = draft[m.id] || [];
    const { error: e } = await supabase.from("profiles").update({ team_coins: coins }).eq("id", m.id);
    setSavingId(null);
    if (e) { setError(e.message); return; }
    setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, team_coins: coins } : x)));
    setSavedId(m.id);
  }

  const memberName = (m) =>
    [m.first_name, m.last_name].filter(Boolean).join(" ").trim() || m.email || m.id.slice(0, 8);

  const readyCoins = useMemo(() => COINS, []);

  if (profileLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-[#f6f6f4]"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-900" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <div className="text-4xl" aria-hidden>🔒</div>
        <h1 className="mt-4 font-serif text-2xl font-semibold text-gray-900">Réservé à l'administrateur</h1>
        <p className="mt-2 text-sm text-gray-600">La gestion des accès de l'équipe est réservée aux administrateurs.</p>
        <Link to="/admin/dashboard" className="mt-6 inline-block rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800">Retour à l'admin</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f4]">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-6 py-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">Administration</p>
            <h1 className="font-serif text-2xl font-semibold text-gray-950">Équipe &amp; accès</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {loading ? "…" : "Actualiser"}
            </button>
            <Link to="/admin/dashboard" className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">← Admin</Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-7">
        <p className="mb-5 max-w-2xl text-sm text-gray-600">
          Attribuez à chaque membre de l'équipe les <strong>coins</strong> (espaces de travail) auxquels il peut accéder.
          Utilisez un <strong>preset</strong> pour l'onboarder en un clic. Un administrateur voit tous les coins.
        </p>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="py-16 text-center text-gray-400">Chargement…</div>
        ) : members.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Aucun membre <code>team</code> pour le moment.
            <div className="mt-2 text-sm text-gray-500">
              Attribuez d'abord le rôle « team » à une personne depuis{" "}
              <Link to="/admin/dashboard" className="text-blue-600 underline">Rôles &amp; permissions</Link>.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {members.map((m) => {
              const coins = draft[m.id] || [];
              return (
                <div key={m.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-base font-semibold text-gray-950">{memberName(m)}</div>
                      <div className="text-xs text-gray-500">{m.email} · {coins.length} coin(s)</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {savedId === m.id && <span className="text-sm text-emerald-700">✓ Enregistré</span>}
                      <button onClick={() => clearAll(m.id)} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50">Tout retirer</button>
                      <button
                        onClick={() => save(m)}
                        disabled={savingId === m.id || !dirty(m)}
                        className="rounded-lg bg-gray-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-40"
                      >
                        {savingId === m.id ? "…" : "Enregistrer"}
                      </button>
                    </div>
                  </div>

                  {/* Presets */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Presets</span>
                    {PRESETS.map((p) => (
                      <button key={p.key} onClick={() => applyPreset(m.id, p.coins)} title={p.desc}
                        className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 hover:border-gray-300 hover:bg-white">
                        + {p.label}
                      </button>
                    ))}
                  </div>

                  {/* Coins */}
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {readyCoins.map((c) => {
                      const on = coins.includes(c.key);
                      return (
                        <button key={c.key} type="button" onClick={() => toggle(m.id, c.key)}
                          className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${on ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white"}`}>
                          <span className="text-xl leading-none" aria-hidden>{c.emoji}</span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold">
                              {c.title}{!c.ready && <span className={`ml-1 text-[10px] ${on ? "text-white/70" : "text-gray-400"}`}>(à venir)</span>}
                            </span>
                            <span className={`mt-0.5 block text-xs ${on ? "text-white/70" : "text-gray-500"}`}>{c.desc}</span>
                          </span>
                          <span className={`ml-auto shrink-0 text-sm ${on ? "text-white" : "text-gray-300"}`}>{on ? "✓" : "○"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
